import axios from 'axios';
import { API_CONFIG } from '../utils/env';
import { secureConsole, sanitizeData } from '../utils/security';
import { authStorage } from '../utils/authStorage';

const base_url = API_CONFIG.BASE_URL;

// Create axios instance with default config
// Do not send CORS headers (e.g. Access-Control-Allow-Origin) from the client – they are response-only and trigger preflight errors.
const apiClient = axios.create({
  baseURL: base_url,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const loginData = authStorage.getLoginData();
    const token = loginData && typeof loginData === 'object' && loginData.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Only log in development, and sanitize sensitive data
    secureConsole.log('API Request:', {
      method: config.method,
      url: config.url,
      baseURL: config.baseURL,
      headers: sanitizeData(config.headers),
      data: sanitizeData(config.data)
    });
    return config;
  },
  (error) => {
    secureConsole.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // Only log in development, and sanitize sensitive data
    secureConsole.log('API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      data: sanitizeData(response.data)
    });
    return response;
  },
  (error) => {
    secureConsole.error('API Error:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: sanitizeData(error.response?.data),
      url: error.config?.url
    });
    
    if (error.response?.status === 401) {
      authStorage.clearLoginData();
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth Services
export const authService = {
  login: async (credentials) => {
    const response = await apiClient.post('/v1/login', credentials);
    return response.data;
  },
  
  logout: async () => {
    const response = await apiClient.post('/v1/logout');
    return response.data;
  },
  
  refreshToken: async () => {
    const response = await apiClient.post('/v1/refresh-token');
    return response.data;
  },
};

// Project Services
export const projectService = {
  getAll: async () => {
    const response = await apiClient.get('/v1/projects');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/v1/projects/${id}`);
    return response.data;
  },
  
  create: async (projectData) => {
    const response = await apiClient.post('/v1/projects', projectData);
    return response.data;
  },
  
  update: async (id, projectData) => {
    const response = await apiClient.put(`/v1/projects/${id}`, projectData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/v1/projects/${id}`);
    return response.data;
  },
};

// User Services
export const userService = {
  getAll: async () => {
    const response = await apiClient.get('/v1/users');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await apiClient.get(`/v1/users/${id}`);
    return response.data;
  },
  
  create: async (userData) => {
    const response = await apiClient.post('/v1/users', userData);
    return response.data;
  },
  
  update: async (id, userData) => {
    const response = await apiClient.put(`/v1/users/${id}`, userData);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await apiClient.delete(`/v1/users/${id}`);
    return response.data;
  },
};

// Release Services
export const releaseService = {
  // Get release details for specific project and participant
  // URL format: /v1/get-release-details/${projId}/${participantId}
  getReleaseDetails: async (urlPath) => {
    const response = await apiClient.get(`/v1/get-release-details/${urlPath}`);
    return response.data;
  },
  
  // Get participant selling data for specific project
  // URL format: /v1/get-release-participant-selling-data/${projId}
  getReleaseParticipantSellingData: async (projId) => {
    const response = await apiClient.get(`/v1/get-release-participant-selling-data/${projId}`);
    return response.data;
  },
  
  // Save new release details
  saveReleaseDetails: async (releaseData) => {
    const response = await apiClient.post('/v1/save-release-details', releaseData);
    return response.data;
  },
  
  // Save selling price for release
  saveSellingPrice: async (sellingData) => {
    const response = await apiClient.post('/v1/save-selling-price', sellingData);
    return response.data;
  },
  
  // Save release ROI percentage
  saveReleaseROI: async (roiData) => {
    const response = await apiClient.post('/v1/save-release-roi-per', roiData);
    return response.data;
  },
  
  // Get vesting schedule for all projects
  getVestingSchedule: async () => {
    const response = await apiClient.get('/v1/get-project-vesting-schedule');
    return response.data;
  },
};

// Generic API call function
export const apiCall = async (endpoint, method = 'GET', data = null) => {
  const config = {
    method,
    url: endpoint,
    ...(data && { data })
  };
  
  const response = await apiClient(config);
  return response.data;
};

export { apiClient };
export default apiClient;
